<template>
  <teleport to="body">
    <div :class="fadeTransition" class="modal">
      <div class="modal-body" :class="bounceTransition">
        <div class="modal-header">
          <h1>Select Your Label Shape</h1>
          <div class="close-svg" v-on:click="closeModal">
            <CloseSVG :propId="'shapes-modal-close'" />
          </div>
        </div>
        <div id="modal-shapes-container">
          <div class="modal-shapes" v-for="shape in labelShapes" :key="shape.id" :id="shape.id">
            <input
              v-if="shape.id === currentLabel.shape_id"
              checked
              name="labelShapeInput"
              type="radio"
              :id="shape.name"
              :value="shape.name"
              @input="handleInput($event.target, shape.id)"
            />
            <input
              v-else
              name="labelShapeInput"
              type="radio"
              :id="shape.name"
              :value="shape.name"
              @input="handleInput($event.target, shape.id)"
            />
            <label :for="shape.name" class="label-option">
              <img :src="require(`@/assets/products/shapes/${shape.id}-a.png`)" />
              {{ shape.name }}
            </label>
          </div>
        </div>

        <button
          aria-label="Change label shape"
          class="modal-select-btn"
          @click="changeLabelShapes()"
        >Change Label Shape</button>
      </div>
    </div>
  </teleport>
</template>

<script lang="ts">
import { defineComponent, ref, Ref, inject, computed, onMounted } from "vue";
import { store } from "@/store";
import CloseSVG from "@/components/CloseSVG.vue";
import { ActionTypes } from "@/store/actions";
import { LabelShapes, CurrentLabel } from "@/types";

export default defineComponent({
  components: {
    CloseSVG,
  },
  setup() {
    const closeModal = inject("closeModal") as () => void;
    const bounceTransition = inject("bounceTransition") as Ref<string>;
    const fadeTransition = inject("fadeTransition") as Ref<string>;

    const labelShapes = computed<LabelShapes[]>(() =>
      store.getters.labelShapes.sort((a: LabelShapes, b: LabelShapes) => {
        return a.editor_position! - b.editor_position!;
      })
    );

    const currentLabel = computed<CurrentLabel>(() => store.getters.currentLabel);
    const currentLabelShape = computed<LabelShapes>(
      () =>
        labelShapes.value.find((label: LabelShapes) => label.id === currentLabel.value.shape_id)!
    );

    onMounted(() => {
      const foundShape = labelShapes.value.find(
        (label: LabelShapes) => label.id === currentLabel.value.shape_id
      );

      if (foundShape && foundShape.id) {
        const shapeContainer = document.getElementById(foundShape.id.toString());

        if (shapeContainer) {
          shapeContainer.style.background = "#0096ff15";

          const listContainer = document.getElementById("modal-shapes-container");

          if (listContainer) {
            const sizeContainerBoundingRect = shapeContainer.getBoundingClientRect();
            const listContainerBoundingRect = listContainer.getBoundingClientRect();

            // prettier-ignore
            listContainer.scroll(0, sizeContainerBoundingRect.top - listContainerBoundingRect.top - sizeContainerBoundingRect.height / 2);
          }
        }
      }
    });

    const currentlySelectedShapeId = ref(currentLabel.value.shape_id);
    const selectedLabelShape = ref({} as LabelShapes);

    const validateTarget = (target: EventTarget | null): target is HTMLInputElement => {
      return target !== null && target !== undefined;
    };

    const handleInput = (target: EventTarget | null, id: number): void => {
      if (validateTarget(target)) {
        const selectedShape = target.value;
        const foundShape = labelShapes.value.find(
          (label: LabelShapes) => label.name === selectedShape
        );

        if (foundShape) {
          const shapeContainer = document.getElementById(id.toString());
          const prevShapeContainer = document.getElementById(
            currentlySelectedShapeId.value.toString()
          );

          if (shapeContainer && prevShapeContainer) {
            prevShapeContainer.style.background = "";

            shapeContainer.style.background = "#0096ff15";
            currentlySelectedShapeId.value = id;
          }

          selectedLabelShape.value = foundShape;
        }
      }
    };

    const changeLabelShapes = () => {
      store.dispatch(ActionTypes.CHANGE_LABEL_SHAPE, selectedLabelShape.value);
      closeModal();
    };

    return {
      closeModal,
      bounceTransition,
      fadeTransition,
      labelShapes,
      currentLabel,
      currentLabelShape,
      selectedLabelShape,
      handleInput,
      changeLabelShapes,
    };
  },
});
</script>

<style scoped>
.modal-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  background-color: white;
  width: 400px;
  height: 500px;
  padding: 20px;
  border-radius: 6px;
  overflow: auto;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 5%;
}

.modal-header h1 {
  font-weight: 600;
  font-size: 22px;
  letter-spacing: -0.25px;
}

.close {
  border-radius: 50%;
  border: solid 1px black;
  background-color: white;
  height: 25px;
  width: 25px;
}

#modal-shapes-container {
  overflow-y: scroll;
  height: 350px;
  width: 100%;
  border: 1px solid rgb(164, 160, 160);
}

.modal-shapes {
  height: 100px;
  border: 1px solid rgb(229, 229, 229);
  padding: 10px;
  margin: 10px;
  display: flex;
  align-items: center;
}

.label-option {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  padding: 17px;
  font-size: 14px;
  font-weight: 700;
}

.label-option img {
  margin-right: 10px;
  width: 50px;
  height: 50px;
}

.modal-select-btn {
  border: none;
  border-radius: 2px;
  background: #ff6c00;
  color: white;
  font-size: 16px;
  height: 40px;
  padding: 0 2px 0 2px;
  text-decoration: none;
  text-align: center;
  width: 100%;
  text-shadow: none;
}
</style>
